import * as React from "react";
import { X } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Filter chips replaced the collapsible filter panel on `/descubrir`: what is
 * applied is always visible, and removable in one click. A filter hidden behind
 * a button is a filter people forget they set.
 *
 * `dashed` means "not yet real" — the `+ Agregar` affordance. Dotted and dashed
 * borders carry that meaning consistently across the system (the upload
 * dropzone, the undo bar), so do not use one for decoration.
 */
export const chipVariants = cva(
  [
    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-pill",
    "px-3.5 py-[7px] text-meta leading-tight",
    "transition-colors",
  ].join(" "),
  {
    variants: {
      variant: {
        active: "bg-steel-600 text-white border border-transparent font-semibold",
        default:
          "bg-white text-neutral-600 border border-neutral-200 font-medium hover:bg-steel-50 hover:text-steel-600 hover:border-steel-600",
        accent: "bg-steel-50 text-steel-600 border border-steel-100 font-semibold",
        neutral: "bg-neutral-100 text-neutral-600 border border-transparent font-medium",
        dashed: "bg-transparent text-neutral-500 border border-dashed border-neutral-300 font-medium hover:border-steel-600 hover:text-steel-600",
        onImage: "bg-on-image-chip text-white border border-transparent font-semibold text-chip px-2.5 py-1",
      },
      interactive: {
        true: "cursor-pointer",
        false: "cursor-default",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: true,
    },
  },
);

export interface ChipProps
  extends
    Omit<React.HTMLAttributes<HTMLSpanElement>, "onSelect">,
    VariantProps<typeof chipVariants> {
  icon?: React.ReactNode;
  /** Renders the × and calls back. The × stops propagation, so a removable chip can also be clickable. */
  onRemove?: () => void;
  /** Announced to screen readers when the chip is a removable filter. */
  removeLabel?: string;
}

const Chip = React.forwardRef<HTMLSpanElement, ChipProps>(
  (
    { className, variant, interactive, icon, onRemove, removeLabel, children, ...props },
    ref,
  ) => (
    <span
      ref={ref}
      className={cn(chipVariants({ variant, interactive, className }))}
      {...props}
    >
      {icon}
      {children}
      {onRemove && (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="-mr-0.5 inline-flex rounded-full opacity-80 transition-opacity hover:opacity-100"
        >
          <X className="size-3" strokeWidth={2.4} />
        </button>
      )}
    </span>
  ),
);
Chip.displayName = "Chip";

export { Chip };
